import { api } from "../../api";
import { h } from "../shared/safe";
import { pill } from "../shared/blocks";
import { GBP_STATUSES, GBP_STATUS_LABELS, describeProblem, parseGbp, readGbpProfile, type GbpProfile, type PublishingStatement } from "./factsModel";
import { busyButton, card, labelled, notice, problemNotice, select, textArea, textInput, webLink, whoText } from "./kit";
import { enc, type Workspace } from "./workspace";

// GBP RECORDS: reference data and link targets (Maps/profile/review links, listing details). CloudPush has NO Google Business Profile
// publishing: it cannot create or edit posts, listings or reviews on Google. The card says so first and in words, from what the
// server reports (`publishing.directApi`, `publishing.statement`), and never implies more.

const FIELD_KEYS = ["label", "listingName", "mapsUrl", "publicUrl", "reviewUrl", "placeId", "locationResource", "primaryCategory", "address", "phone", "note"] as const;
type FieldKey = (typeof FIELD_KEYS)[number];
const FIELD_LABELS: Record<FieldKey, string> = { label: "Label", listingName: "Listing name", mapsUrl: "Maps link", publicUrl: "Public profile link", reviewUrl: "Review link", placeId: "Place id", locationResource: "Location resource name", primaryCategory: "Primary category", address: "Address", phone: "Phone", note: "Note" };

export function publishingBanner(p: PublishingStatement | null): HTMLElement {
  const stated = p?.directApi;
  return h(
    "div",
    { class: `fx-gbp-banner${stated === false ? "" : " fx-gbp-banner--unsure"}`, role: "note", id: "fx-gbp-banner", dataset: { directApi: stated === null || stated === undefined ? "unknown" : String(stated) } },
    h("strong", null, stated === false ? "Reference data only. CloudPush has NO direct Google Business Profile publishing." : stated === true ? "The server reports direct GBP publishing (directApi: true). This page has not verified that." : "The server did not say whether GBP publishing exists. Treat these records as reference data only."),
    " ",
    pill(`directApi: ${stated === null || stated === undefined ? "not stated" : String(stated)}`, stated === false ? "neutral" : "warn"),
    h("p", { class: "fx-small" }, p?.statement ?? "A Maps or profile link on this page is a link target. It does not mean CloudPush can post to, edit or manage the Google listing."),
  );
}

export function gbpCard(ws: Workspace): HTMLElement {
  const body = h("div");
  const out = card("fx-gbp", "Google Business Profile records", body);
  let profiles: GbpProfile[] | null = null;
  let publishing: PublishingStatement | null = null;
  let loadError: string | null = null;
  let flash: HTMLElement | null = null;
  let editing: string | "new" | null = null;

  async function load() {
    try {
      const g = parseGbp(await api<unknown>(`/v1/clients/${enc(ws.clientId)}/gbp`));
      if (!ws.life.alive()) return;
      profiles = g?.profiles ?? null;
      publishing = g?.publishing ?? null;
      loadError = g ? null : "The server sent GBP records this page does not understand.";
    } catch (e) {
      if (!ws.life.alive()) return;
      loadError = describeProblem(e, "GBP records could not be loaded").message;
    }
    paint();
  }

  function profileBlock(p: GbpProfile): HTMLElement {
    const flagNote = (k: "mapsUrl" | "publicUrl" | "reviewUrl") => (p.urlFlags[k] === false ? h("span", { class: "muted fx-small" }, " (does not look like a Google Maps link)") : null);
    const li = h(
      "li",
      { class: "fx-gbp-item", dataset: { gbpId: p.id, status: p.status } },
      h("div", { class: "fx-unr-head" }, h("strong", null, p.label), pill(GBP_STATUS_LABELS[p.status] ?? p.status, p.status === "owner_confirmed" ? "ok" : "neutral"), h("span", { class: "tag" }, "reference data")),
      h(
        "dl",
        { class: "deflist" },
        ...(p.listingName ? [h("dt", null, "Listing name"), h("dd", null, p.listingName)] : []),
        ...(p.mapsUrl ? [h("dt", null, "Maps link"), h("dd", null, webLink(p.mapsUrl), flagNote("mapsUrl"))] : []),
        ...(p.publicUrl ? [h("dt", null, "Public profile"), h("dd", null, webLink(p.publicUrl), flagNote("publicUrl"))] : []),
        ...(p.reviewUrl ? [h("dt", null, "Review link"), h("dd", null, webLink(p.reviewUrl), flagNote("reviewUrl"))] : []),
        ...(p.placeId ? [h("dt", null, "Place id"), h("dd", null, p.placeId)] : []),
        ...(p.locationResource ? [h("dt", null, "Location resource"), h("dd", null, p.locationResource)] : []),
        ...(p.address ? [h("dt", null, "Address"), h("dd", null, p.address)] : []),
        ...(p.phone ? [h("dt", null, "Phone"), h("dd", null, p.phone)] : []),
        ...(p.primaryCategory ? [h("dt", null, "Category"), h("dd", null, p.primaryCategory)] : []),
      ),
      p.note ? h("div", { class: "muted fx-small" }, `Note: ${p.note}`) : null,
      h("div", { class: "muted fx-small" }, `Last updated by ${whoText(p.updatedBy)}${p.updatedAt ? ` · ${p.updatedAt.text}` : ""}`),
    );
    if (editing === p.id) li.append(profileForm(p));
    else li.append(h("div", { class: "btn-group" }, h("button", { type: "button", class: "secondary", dataset: { action: "edit" }, "aria-label": `Edit GBP record ${p.label}`, on: { click: () => { editing = p.id; flash = null; paint(); out.querySelector<HTMLElement>(`[data-gbp-id="${p.id}"] input`)?.focus(); } } }, "Edit")));
    return li;
  }

  function profileForm(p: GbpProfile | null): HTMLElement {
    const vals: Record<FieldKey, string> = Object.fromEntries(FIELD_KEYS.map((k) => [k, (p ? (p[k] as string | null) : "") ?? ""])) as Record<FieldKey, string>;
    let status = p?.status ?? "unverified";
    const result = h("div", { "aria-live": "polite" });
    const f = h("form", { class: "fx-form", novalidate: true, "aria-label": p ? `Edit GBP record ${p.label}` : "Add a GBP record" });
    const save = busyButton({
      label: p ? "Save record" : "Add record",
      busyLabel: "Saving…",
      type: "submit",
      id: p ? `fx-gbp-save-${p.id}` : "fx-gbp-add",
      onClick: async () => {
        if (vals.label.trim() === "") return void result.replaceChildren(notice("bad", "Give the record a label. Nothing was saved."));
        const payload: Record<string, unknown> = {};
        for (const k of FIELD_KEYS) {
          const v = vals[k].trim();
          const before = p ? (((p[k] as string | null) ?? "")).trim() : "";
          if (!p ? v !== "" : v !== before) payload[k] = v === "" ? null : v;
        }
        if (p && status !== p.status) payload.status = status;
        if (!p && status !== "unverified") payload.status = status;
        if (p && Object.keys(payload).length === 0) return void result.replaceChildren(notice("info", "Nothing changed."));
        try {
          const res = await api<{ profile?: unknown }>(p ? `/v1/clients/${enc(ws.clientId)}/gbp/profiles/${enc(p.id)}` : `/v1/clients/${enc(ws.clientId)}/gbp/profiles`, { method: p ? "PATCH" : "POST", body: JSON.stringify(payload) });
          if (!ws.life.alive()) return;
          const saved = readGbpProfile(res.profile);
          editing = null;
          flash = notice("ok", `${p ? "Saved" : "Added"} the reference record${saved ? ` "${saved.label}"` : ""}. Nothing was sent to Google.`);
          await load();
          await ws.refreshFacts();
        } catch (e) {
          if (!ws.life.alive()) return;
          result.replaceChildren(problemNotice(describeProblem(e, "Not saved")));
        }
      },
    });
    f.onsubmit = (e) => {
      e.preventDefault();
      void save.click();
    };
    f.append(
      h("div", { class: "fx-grid" }, ...FIELD_KEYS.map((k) => labelled(FIELD_LABELS[k], k === "note" ? textArea(vals[k], { name: k, rows: 2, onInput: (v) => void (vals[k] = v) }) : textInput(vals[k], { name: k, onInput: (v) => void (vals[k] = v) }), { wide: k === "note" })), labelled("Status", select(GBP_STATUSES.map((s): [string, string] => [s, GBP_STATUS_LABELS[s] ?? s]), status, { name: "status", onChange: (v) => void (status = v) }))),
      result,
      h("div", { class: "btn-group" }, save, h("button", { type: "button", class: "secondary", on: { click: () => { editing = null; paint(); } } }, "Cancel")),
    );
    return f;
  }

  function paint() {
    const active = profiles?.filter((p) => p.status !== "archived") ?? [];
    const archived = profiles?.filter((p) => p.status === "archived") ?? [];
    body.replaceChildren(
      publishingBanner(publishing ?? ws.facts?.publishing ?? null),
      ...(flash ? [flash] : []),
      loadError
        ? h("div", { class: "error-banner", role: "alert" }, loadError, " ", h("button", { type: "button", class: "secondary", on: { click: () => void load() } }, "Try again"))
        : profiles === null
          ? h("p", { class: "muted" }, "Loading…")
          : active.length === 0
            ? h("p", { class: "muted" }, "No GBP records yet.")
            : h("ul", { class: "fx-gbp-list", "aria-label": "GBP records" }, ...active.map(profileBlock)),
      ...(archived.length ? [h("details", null, h("summary", null, `Archived records (${archived.length}); not link targets`), h("ul", { class: "fx-gbp-list" }, ...archived.map(profileBlock)))] : []),
      editing === "new" ? h("div", null, h("h3", null, "Add a GBP record"), profileForm(null)) : h("button", { type: "button", id: "fx-gbp-new", on: { click: () => { editing = "new"; flash = null; paint(); out.querySelector<HTMLElement>("#fx-gbp-new, form input")?.focus(); } } }, "+ Add a GBP record"),
    );
  }

  void load();
  paint();
  return out;
}
